'use client';

import { useEffect, useState, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { format, isValid, isToday, isYesterday } from 'date-fns';
import { Bot, MessageSquare, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { cn } from '@/lib/utils';
import { displayId, isInstagramId } from '@/lib/channel';
import { useAutoPolling } from '@/hooks/use-auto-polling';
import { conversationClient, conversationTagsClient, type ConversationTag } from '@/lib/gupshup-client';
import { brandClient } from '@/lib/brand-client';
import { playNotificationSound, startPersistentRing, stopPersistentRing } from '@/lib/notification-display';
import { loadSoundSettings } from '@/lib/notification-sound-settings';
import { NotificationSoundSettingsPopover } from '@/components/notification-sound-settings-popover';
import type { LocationResponseDto } from '@/types/brand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { ContactAvatar } from '@/components/contact-avatar';
import { Badge } from '@/components/ui/badge';


/** Customer mood → emoji/label for the inbox row. Neutral is intentionally NOT shown (no noise). */
const SENTIMENT_EMOJI: Record<string, string> = { angry: '😡', upset: '🙁', happy: '🙂' };
const SENTIMENT_LABEL: Record<string, string> = {
  angry: 'Cliente enojado',
  upset: 'Cliente molesto',
  happy: 'Cliente contento',
  neutral: 'Neutral',
};
const sentimentEmoji = (s?: string | null): string | null => (s ? SENTIMENT_EMOJI[s] ?? null : null);
const sentimentLabel = (s?: string | null): string => (s ? SENTIMENT_LABEL[s] ?? '' : '');

type Conversation = {
  id: string;
  phoneNumber: string;
  status: string;
  lastActiveAt: string;
  lastReadAt?: string;
  aiDisabledUntil?: string;
  handoffUntil?: string;
  preferredLocationId?: string | null;
  tagIds?: string[];
  sentiment?: string | null;
  lastMessagePreview?: string | null;
  /** A DRAFT order is open on this conversation right now. Drives the cart poll cadence upstream. */
  hasActiveOrder?: boolean;
  metadata?: Record<string, unknown>;
  contactName?: string;
  messagesCount?: number;
  lastMessage?: {
    content: string;
    direction: string;
    type?: string;
  };
};

function formatConversationDate(timestamp: string, t: TFunction): string {
  try {
    const date = new Date(timestamp);
    if (!isValid(date)) return '';

    if (isToday(date)) return format(date, 'HH:mm');
    if (isYesterday(date)) return t('list.yesterday');
    return format(date, 'MMM d');
  } catch {
    return '';
  }
}

function isConversationUnread(conversation: Conversation): boolean {
  // If there's no lastReadAt, conversation is considered unread
  if (!conversation.lastReadAt) {
    return true;
  }

  // If lastActiveAt (last message time) is after lastReadAt, there are unread messages
  const lastMessageTime = new Date(conversation.lastActiveAt).getTime();
  const lastReadTime = new Date(conversation.lastReadAt).getTime();

  return lastMessageTime > lastReadTime;
}

type Props = {
  onSelectConversation: (conversation: Conversation) => void;
  selectedConversationId?: string;
  /**
   * Fired with the selected conversation's fresh row after every poll. The parent holds a snapshot
   * taken at click time, and this list is the only thing that re-reads the flags on it — above all
   * `hasActiveOrder`, which paces the cart poll behind the header's order button.
   */
  onSelectedConversationRefreshed?: (conversation: Conversation) => void;
  sidebarOpen: boolean;
  onCloseSidebar: () => void;
  handoffCount?: number;
  /** Hide the list on md/lg so the chat and the cart panel both fit. */
  desktopCollapsed?: boolean;
};

export type ConversationListRef = {
  refresh: () => Promise<Conversation[]>;
  selectByPhoneNumber: (phoneNumber: string) => void;
};

export const ConversationList = forwardRef<ConversationListRef, Props>(
  (
    {
      onSelectConversation,
      selectedConversationId,
      onSelectedConversationRefreshed,
      sidebarOpen,
      onCloseSidebar,
      handoffCount,
      desktopCollapsed,
    },
    ref,
  ) => {
  const { t } = useTranslation('conversations');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  // Ring the notification chime the moment a conversation enters handoff. Detected from the polled
  // list (same signal that paints the row red) rather than the SSE stream, so it works regardless of
  // how notifications are targeted server-side. `seenHandoff` is seeded on first load so existing
  // handoffs don't ring on mount.
  const seenHandoffIds = useRef<Set<string>>(new Set());
  const handoffSeeded = useRef(false);
  useEffect(() => {
    const now = Date.now();
    const current = new Set(
      conversations
        .filter((c) => c.handoffUntil && new Date(c.handoffUntil).getTime() > now)
        .map((c) => c.id),
    );
    if (!handoffSeeded.current) {
      handoffSeeded.current = true;
      seenHandoffIds.current = current;
      return;
    }
    let hasNew = false;
    for (const id of current) {
      if (!seenHandoffIds.current.has(id)) {
        hasNew = true;
        break;
      }
    }
    seenHandoffIds.current = current;

    // "Ring until attended": while ANY conversation is still waiting for a human, keep the cycle
    // running; the moment the queue empties it stops. Driven off the same polled list, so it can
    // never keep ringing for work that is already handled.
    if (loadSoundSettings().untilAttended) {
      if (current.size > 0) startPersistentRing();
      else stopPersistentRing();
      return;
    }
    stopPersistentRing();
    if (hasNew) playNotificationSound();
  }, [conversations]);

  // Never leave a cycle ringing behind an unmounted inbox.
  useEffect(() => stopPersistentRing, []);
  // Keep the opened conversation in sync with what the 2s poll knows about it. The parent decides
  // what (if anything) to take from the fresh row; here we only make sure it sees one.
  useEffect(() => {
    if (!selectedConversationId || !onSelectedConversationRefreshed) return;
    const row = conversations.find((conv) => conv.id === selectedConversationId);
    if (row) onSelectedConversationRefreshed(row);
  }, [conversations, selectedConversationId, onSelectedConversationRefreshed]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'manual' | 'handoff' | 'unread'>('all');
  const [locations, setLocations] = useState<LocationResponseDto[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string | undefined>(undefined);
  const [tags, setTags] = useState<ConversationTag[]>([]);
  const [selectedTagId, setSelectedTagId] = useState<string | undefined>(undefined);
  // Channel scope for the shared inbox: 'all' shows WhatsApp + Instagram together.
  const [selectedChannel, setSelectedChannel] = useState<'all' | 'whatsapp' | 'instagram'>('all');
  const [resumingId, setResumingId] = useState<string | null>(null);

  // Load the brand's locations so operators can scope the inbox to their sucursal.
  useEffect(() => {
    brandClient
      .listLocations()
      .then(setLocations)
      .catch((error) => console.error('Error fetching locations:', error));
  }, []);

  // Load the brand's conversation tags (status labels) for the chips + filter.
  // Poll every 30s so newly-created tag definitions (e.g. "requiere intervencion")
  // show up without needing a full page reload. Cheap: tags are few and rarely change.
  const fetchTags = useCallback(() => {
    conversationTagsClient
      .list()
      .then(setTags)
      .catch((error) => console.error('Error fetching conversation tags:', error));
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useAutoPolling({
    interval: 30000,
    enabled: true,
    onPoll: fetchTags,
  });

  const fetchConversations = useCallback(async (page: number = 1, append: boolean = false, merge: boolean = false, filter?: 'manual' | 'handoff' | 'unread'): Promise<Conversation[]> => {
    try {
      const response = await conversationClient.conversations.list({
        page,
        limit: 50,
        filter,
        locationId: selectedLocationId,
        tagId: selectedTagId,
        channel: selectedChannel === 'all' ? undefined : selectedChannel,
      });

      // Transform conversations to match frontend expectations
      const transformedData = response.data.map((conversation) => ({
        id: conversation.id,
        phoneNumber: conversation.customerPhone,
        status: 'active',
        lastActiveAt: conversation.lastMessageAt,
        lastReadAt: conversation.lastReadAt,
        aiDisabledUntil: conversation.aiDisabledUntil,
        handoffUntil: conversation.handoffUntil,
        preferredLocationId: conversation.preferredLocationId,
        tagIds: conversation.tagIds ?? [],
        sentiment: conversation.sentiment ?? null,
        lastMessagePreview: conversation.lastMessagePreview ?? null,
        hasActiveOrder: conversation.hasActiveOrder ?? false,
        metadata: {},
        contactName: conversation.customerName,
        messagesCount: undefined,
        lastMessage: undefined,
      }));

      // Update pagination metadata
      setTotalPages(response.meta.totalPages);
      if (!merge) {
        setCurrentPage(response.meta.currentPage);
      }

      // Handle different update modes
      if (merge) {
        // Merge mode: Update existing conversations from page 1, keep additional pages
        setConversations(prev => {
          // Create a map of new data by ID for efficient lookup
          const newDataMap = new Map(transformedData.map(conv => [conv.id, conv]));

          // Update existing conversations with new data from page 1
          const updated = prev.map(conv =>
            newDataMap.has(conv.id) ? newDataMap.get(conv.id)! : conv
          );

          // Add any new conversations from page 1 that don't exist yet (at the beginning)
          const existingIds = new Set(prev.map(c => c.id));
          const newConversations = transformedData.filter(conv => !existingIds.has(conv.id));

          // Re-sort by lastActiveAt descending so conversations shuffle live
          return [...newConversations, ...updated].sort(
            (a, b) => new Date(b.lastActiveAt).getTime() - new Date(a.lastActiveAt).getTime()
          );
        });
      } else if (append) {
        // Append mode: Add new page to the end
        setConversations(prev => [...prev, ...transformedData]);
      } else {
        // Replace mode: Replace all conversations
        setConversations(transformedData);
      }
      return transformedData;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return [];
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  }, [selectedLocationId, selectedTagId, selectedChannel]);

  const currentFilterParam = activeFilter === 'all' ? undefined : activeFilter;

  useEffect(() => {
    fetchConversations(1, false, false, currentFilterParam);
  }, [fetchConversations, currentFilterParam]);

  const handleRefresh = () => {
    setRefreshing(true);
    setCurrentPage(1);
    fetchConversations(1, false, false, currentFilterParam);
  };

  const handleLoadMore = () => {
    if (currentPage < totalPages && !loadingMore) {
      setLoadingMore(true);
      fetchConversations(currentPage + 1, true, false, currentFilterParam);
    }
  };

  const handleFilterChange = (filter: 'all' | 'manual' | 'handoff' | 'unread') => {
    setActiveFilter(filter);
    setCurrentPage(1);
    setLoading(true);
    fetchConversations(1, false, false, filter === 'all' ? undefined : filter);
  };

  // Changing the sucursal re-fetches automatically (selectedLocationId is a fetchConversations dep).
  const handleLocationChange = (locationId: string) => {
    setSelectedLocationId(locationId || undefined);
    setCurrentPage(1);
    setLoading(true);
  };

  const handleTagChange = (tagId: string) => {
    setSelectedTagId(tagId || undefined);
    setCurrentPage(1);
    setLoading(true);
  };

  const locationNameById = new Map(locations.map((loc) => [loc.id, loc.name]));
  const tagById = new Map(tags.map((t) => [t.id, t]));

  // Auto-polling for conversations (every 2 seconds) so agent-applied tags / mood / new
  // messages surface almost immediately. Paused automatically while the tab is hidden
  // (see useAutoPolling), and only page 1 is merged, so the extra frequency stays cheap.
  const { isPolling } = useAutoPolling({
    interval: 2000,
    enabled: true,
    onPoll: async () => {
      // merge mode: update page 1 data without losing loaded pages
      await fetchConversations(1, false, true, currentFilterParam);
    }
  });

  // Quick action on a list row: clear the handoff/manual flags so the AI takes the
  // conversation back, without having to open the chat first.
  const handleResumeAi = async (e: React.SyntheticEvent, conversation: Conversation) => {
    e.stopPropagation();
    if (resumingId) return;
    setResumingId(conversation.id);
    try {
      await conversationClient.conversations.resumeAi(conversation.id);
      // Optimistically clear the flags; the next poll reconciles with the backend.
      setConversations(prev =>
        prev.map(conv =>
          conv.id === conversation.id
            ? { ...conv, aiDisabledUntil: undefined, handoffUntil: undefined }
            : conv
        )
      );
      // Keep the open chat header in sync when the resumed chat is the selected one
      if (conversation.id === selectedConversationId) {
        onSelectConversation({ ...conversation, aiDisabledUntil: undefined, handoffUntil: undefined });
      }
      toast.success(t('list.aiResumed'));
    } catch (error) {
      console.error('Error resuming AI:', error);
      toast.error(t('list.aiResumeError'));
    } finally {
      setResumingId(null);
    }
  };

  const handleSelectConversation = (conversation: Conversation) => {
    // Optimistically mark as read in local state for immediate UI feedback
    // The backend will actually mark it as read when messages are fetched
    if (isConversationUnread(conversation)) {
      setConversations(prev =>
        prev.map(conv =>
          conv.id === conversation.id
            ? { ...conv, lastReadAt: new Date().toISOString() }
            : conv
        )
      );
    }

    onSelectConversation(conversation);
  };

  const selectByPhoneNumber = (phoneNumber: string) => {
    const conversation = conversations.find(conv => conv.phoneNumber === phoneNumber);
    if (conversation) {
      handleSelectConversation(conversation);
    }
  };

  useImperativeHandle(ref, () => ({
    refresh: async () => {
      setRefreshing(true);
      setCurrentPage(1);
      // Return the freshly fetched list — the `conversations` state variable here
      // is a stale closure from the previous render.
      return fetchConversations(1, false, false);
    },
    selectByPhoneNumber
  }), [fetchConversations, selectByPhoneNumber]);

  const filteredConversations = conversations.filter((conv) => {
    const query = searchQuery.toLowerCase();
    return (
      conv.phoneNumber.toLowerCase().includes(query) ||
      conv.contactName?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <>
        {/* Backdrop for mobile */}
        {sidebarOpen && (
          <div
            className="fixed top-14 inset-x-0 bottom-0 bg-black/50 z-40 md:hidden animate-in fade-in duration-200"
            onClick={onCloseSidebar}
          />
        )}
        <div className={cn(
          "w-full md:w-96 border-r border-chat-border bg-chat-surface flex flex-col",
          "fixed md:relative top-14 md:top-0 bottom-0 left-0 z-50 md:z-auto",
          "transition-transform duration-300 ease-in-out md:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}>
          <div className="p-4 border-b border-chat-border bg-chat-panel">
            <div className="flex items-center justify-between mb-3">
              <Skeleton className="h-7 w-20" />
              <Skeleton className="h-9 w-24" />
            </div>
            <Skeleton className="h-10 w-full rounded-lg" />
          </div>
          <div className="flex-1 p-3 space-y-3">
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="flex gap-3 p-3">
                <Skeleton className="h-12 w-12 rounded-full flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Backdrop for mobile */}
      {sidebarOpen && (
        <div
          className="fixed top-14 inset-x-0 bottom-0 bg-black/50 z-40 md:hidden"
          onClick={onCloseSidebar}
        />
      )}
      <div className={cn(
        "w-full md:w-96 border-r border-chat-border bg-chat-surface flex flex-col",
        "fixed md:relative top-14 md:top-0 bottom-0 left-0 z-50 md:z-auto",
        "transition-transform duration-300 ease-in-out md:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full",
        // The cart panel takes a column of its own; below xl there is not room for all three, so
        // the list steps aside on desktop. It stays reachable as the mobile drawer.
        desktopCollapsed && "md:hidden xl:flex"
      )}>
      <div className="p-4 border-b border-chat-border bg-chat-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-chat-text">{t('list.title')}</h1>
              {isPolling && (
                <div
                  className="h-2 w-2 rounded-full bg-green-500 animate-pulse"
                  title={t('list.autoUpdating')}
                />
              )}
            </div>
            <div className="flex items-center gap-1">
              <NotificationSoundSettingsPopover />
              <Button
                onClick={handleRefresh}
                disabled={refreshing}
                variant="ghost"
                size="icon"
                aria-label={t('list.refreshAria')}
                className="text-chat-text-muted hover:bg-chat-border/30"
              >
                <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
              </Button>
            </div>
          </div>
        <div className="relative" role="search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-chat-text-muted" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('list.searchPlaceholder')}
            aria-label={t('list.searchAria')}
            className="pl-9 bg-chat-elevated border-chat-border focus-visible:ring-chat-accent rounded-lg"
          />
        </div>
        <div className="flex items-center gap-1.5 mt-2">
          {(['all', 'manual', 'handoff', 'unread'] as const).map((filter) => {
            const labels = {
              all: t('list.filters.all'),
              manual: t('list.filters.manual'),
              handoff: t('list.filters.handoff'),
              unread: t('list.filters.unread'),
            };
            const activeStyles = {
              all: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700',
              manual: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700',
              handoff: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700',
              unread: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700',
            };
            const isActive = activeFilter === filter;
            return (
              <button
                key={filter}
                onClick={() => handleFilterChange(filter)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                  isActive ? activeStyles[filter] : 'bg-chat-panel text-chat-text-muted border-chat-border hover:bg-chat-hover'
                }`}
              >
                {labels[filter]}
                {filter === 'handoff' && !!handoffCount && (
                  <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none">
                    {handoffCount > 99 ? '99+' : handoffCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {/* Channel scope — both channels share this inbox, so this is how an operator reads only one. */}
        <div className="flex items-center gap-1.5 mt-2">
          {(['all', 'whatsapp', 'instagram'] as const).map((channel) => {
            const labels = {
              all: t('list.channels.all'),
              whatsapp: t('list.channels.whatsapp'),
              instagram: t('list.channels.instagram'),
            };
            const activeStyles = {
              all: 'bg-chat-elevated text-chat-text border-chat-border',
              whatsapp: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700',
              instagram: 'bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300 border-pink-300 dark:border-pink-700',
            };
            const isActive = selectedChannel === channel;
            return (
              <button
                key={channel}
                onClick={() => setSelectedChannel(channel)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                  isActive ? activeStyles[channel] : 'bg-chat-panel text-chat-text-muted border-chat-border hover:bg-chat-hover'
                }`}
              >
                {labels[channel]}
              </button>
            );
          })}
        </div>
        {locations.length > 1 && (
          <div className="mt-2">
            <select
              value={selectedLocationId ?? ''}
              onChange={(e) => handleLocationChange(e.target.value)}
              aria-label={t('list.filterByLocationAria')}
              className="w-full px-2.5 py-1.5 rounded-lg text-xs font-medium border border-chat-border bg-chat-elevated text-chat-text focus-visible:ring-chat-accent"
            >
              <option value="">{t('list.allLocations')}</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {tags.length > 0 && (
          <div className="mt-2">
            <select
              value={selectedTagId ?? ''}
              onChange={(e) => handleTagChange(e.target.value)}
              aria-label={t('list.filterByTagAria')}
              className="w-full px-2.5 py-1.5 rounded-lg text-xs font-medium border border-chat-border bg-chat-elevated text-chat-text focus-visible:ring-chat-accent"
            >
              <option value="">{t('list.allTags')}</option>
              {tags.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <ScrollArea className="flex-1 h-0 overflow-hidden">
        {filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-8 text-center animate-fade-up">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-chat-panel">
              <MessageSquare className="h-6 w-6 text-chat-text-muted" />
            </div>
            <p className="text-sm font-medium text-chat-text">
              {searchQuery ? t('list.noResults') : t('list.empty')}
            </p>
            <p className="text-xs text-chat-text-muted">
              {searchQuery ? t('list.noResultsHint') : t('list.emptyHint')}
            </p>
          </div>
        ) : (
          <div className="w-full overflow-hidden">
          {filteredConversations.map((conversation) => {
            const isUnread = isConversationUnread(conversation);
            // Handoff = the agent asked for a human ("requiere intervención"). Tint the whole row red
            // so it stands out in the list, not just the small badge.
            const isHandoff = !!(conversation.handoffUntil && new Date(conversation.handoffUntil) > new Date());
            const isAiPaused =
              (conversation.aiDisabledUntil && new Date(conversation.aiDisabledUntil) > new Date()) ||
              isHandoff;
            return (
            <button
              key={conversation.id}
              onClick={() => handleSelectConversation(conversation)}
              className={cn(
                'w-full p-3 pr-4 border-b border-chat-border-soft hover:bg-chat-panel text-left transition-colors relative overflow-hidden animate-in fade-in duration-200',
                isHandoff && 'bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-900/40 border-l-4 border-l-red-500',
                selectedConversationId === conversation.id && (isHandoff ? 'bg-red-100 dark:bg-red-900/40' : 'bg-chat-panel')
              )}
            >
              <div className="flex gap-3 items-start overflow-hidden">
                <ContactAvatar
                  contactName={conversation.contactName}
                  phoneNumber={conversation.phoneNumber}
                  className="h-12 w-12 flex-shrink-0"
                  textClassName="text-sm"
                />
                <div className="flex-1 min-w-0 flex justify-between items-start gap-4 overflow-hidden">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center gap-1.5">
                      {sentimentEmoji(conversation.sentiment) && (
                        <span
                          className="shrink-0 text-sm leading-none"
                          title={sentimentLabel(conversation.sentiment)}
                          aria-label={sentimentLabel(conversation.sentiment)}
                        >
                          {sentimentEmoji(conversation.sentiment)}
                        </span>
                      )}
                      <p className={cn(
                        "truncate",
                        isUnread ? "font-semibold text-chat-text" : "font-medium text-chat-text"
                      )}>
                        {conversation.contactName || displayId(conversation.phoneNumber)}
                      </p>
                      {isInstagramId(conversation.phoneNumber) && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300 shrink-0">
                          {t('list.badges.instagram')}
                        </span>
                      )}
                      {conversation.aiDisabledUntil && new Date(conversation.aiDisabledUntil) > new Date() && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 shrink-0">
                          {t('list.badges.manual')}
                        </span>
                      )}
                      {conversation.handoffUntil && new Date(conversation.handoffUntil) > new Date() && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 shrink-0">
                          {t('list.badges.handoff')}
                        </span>
                      )}
                      {locations.length > 1 && conversation.preferredLocationId && locationNameById.get(conversation.preferredLocationId) && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 shrink-0 truncate max-w-[120px]">
                          {locationNameById.get(conversation.preferredLocationId)}
                        </span>
                      )}
                    </div>
                    {conversation.lastMessagePreview && (
                      <p className={cn(
                        "truncate text-xs mt-0.5",
                        isUnread ? "text-chat-text/80 font-medium" : "text-muted-foreground"
                      )}>
                        {conversation.lastMessagePreview}
                      </p>
                    )}
                    {(conversation.tagIds ?? []).some((id) => tagById.has(id)) && (
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        {(conversation.tagIds ?? [])
                          .map((id) => tagById.get(id))
                          .filter((t): t is ConversationTag => Boolean(t))
                          .map((tag) => (
                            <span
                              key={tag.id}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium truncate max-w-[130px]"
                              style={{ backgroundColor: `${tag.color}22`, color: tag.color }}
                            >
                              {tag.name}
                            </span>
                          ))}
                      </div>
                    )}
                    {conversation.lastMessage && (
                      <p className={cn(
                        "text-sm truncate mt-0.5",
                        isUnread ? "text-chat-text font-medium" : "text-chat-text-muted"
                      )}>
                        {conversation.lastMessage.direction === 'outbound' && (
                          <span className="text-chat-receipt-read">✓ </span>
                        )}
                        {conversation.lastMessage.content}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0 mt-0.5 ml-4">
                    <span className={cn(
                      "text-xs",
                      isUnread ? "text-chat-accent font-medium" : "text-chat-text-muted"
                    )}>
                      {formatConversationDate(conversation.lastActiveAt, t)}
                    </span>
                    {isUnread && (
                      <Badge className="bg-chat-accent hover:bg-chat-accent text-white rounded-full h-5 min-w-5 px-1.5 text-xs">
                        •
                      </Badge>
                    )}
                    {/* The row itself is a <button>, so this action is a span to avoid nested buttons */}
                    {isAiPaused && (
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label={t('list.resumeAiTooltip')}
                        title={t('list.resumeAiTooltip')}
                        onClick={(e) => handleResumeAi(e, conversation)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleResumeAi(e, conversation);
                          }
                        }}
                        className={cn(
                          'inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium transition-colors',
                          'text-chat-accent-deep border-chat-accent-deep hover:bg-chat-accent-deep/5',
                          resumingId === conversation.id && 'opacity-50 pointer-events-none'
                        )}
                      >
                        {resumingId === conversation.id ? (
                          <RefreshCw className="h-3 w-3 animate-spin" />
                        ) : (
                          <Bot className="h-3 w-3" />
                        )}
                        {t('list.resumeAi')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
            );
          })
          }
          {/* Pagination controls */}
          {!searchQuery && currentPage < totalPages && (
            <div className="p-4 flex justify-center">
              <Button
                onClick={handleLoadMore}
                loading={loadingMore}
                variant="outline"
                className="w-full max-w-xs"
              >
                {loadingMore
                  ? t('list.loading')
                  : t('list.loadMore', { current: currentPage, total: totalPages })}
              </Button>
            </div>
          )}
          </div>
        )}
      </ScrollArea>
      </div>
    </>
  );
});

ConversationList.displayName = 'ConversationList';
